import { api, ApiResponse } from "../lib/api.ts";
import { AppVersionData, CreateAppVersionData, DiscountData, DiscountRequest, DonationData, PaymentData, PaymentGateway, PaymentGatewayRequest, PaymentStatus, PlanData, PlanRequest, ReplyToTicketRequest, ReportData, SettingData, TicketData, TicketMessageData } from "../entry/server/app.ts";

export const appService = {
    getGateways: async (page: number): Promise<ApiResponse<PaymentGateway[]>> => {
        return await api.get<PaymentGateway[]>({ url: "gateways", queryParams: { "page": page.toString() } });
    },
    getGateway: async (id: number): Promise<ApiResponse<PaymentGateway>> => {
        return await api.get<PaymentGateway>({ url: `gateway/${id}` });
    },
    createGateway: async (gateway: PaymentGatewayRequest): Promise<ApiResponse<any>> => {
        return await api.post<any>({ url: "gateway", body: gateway });
    },
    updateGateway: async (id: number, gateway: PaymentGatewayRequest): Promise<ApiResponse<any>> => {
        return await api.put<any>({ url: `gateway/${id}`, body: gateway });
    },
    deleteGateway: async (id: number): Promise<ApiResponse<any>> => {
        return await api.delete<any>({ url: `gateway/${id}` });
    },

    getDiscounts: async (page: number): Promise<ApiResponse<DiscountData[]>> => {
        return await api.get<DiscountData[]>({ url: "discounts", queryParams: { "page": page.toString() } });
    },
    getDiscount: async (id: number): Promise<ApiResponse<DiscountData>> => {
        return await api.get<DiscountData>({ url: `discount/${id}` });
    },
    createDiscount: async (discount: DiscountRequest): Promise<ApiResponse<any>> => {
        return await api.post<any>({ url: "discount", body: discount });
    },
    updateDiscount: async (id: number, discount: DiscountRequest): Promise<ApiResponse<any>> => {
        return await api.put<any>({ url: `discount/${id}`, body: discount });
    },
    deleteDiscount: async (id: number): Promise<ApiResponse<any>> => {
        return await api.delete<any>({ url: `discount/${id}` });
    },

    getPlans: async (page: number): Promise<ApiResponse<PlanData[]>> => {
        return await api.get<PlanData[]>({ url: "plans", queryParams: { "page": page.toString() } });
    },
    getPlan: async (id: number): Promise<ApiResponse<PlanData>> => {
        return await api.get<PlanData>({ url: `plan/${id}` });
    },
    createPlan: async (plan: PlanRequest): Promise<ApiResponse<any>> => {
        return await api.post<any>({ url: "plan", body: plan });
    },
    updatePlan: async (id: number, plan: PlanRequest): Promise<ApiResponse<any>> => {
        return await api.put<any>({ url: `plan/${id}`, body: plan });
    },
    deletePlan: async (id: number): Promise<ApiResponse<any>> => {
        return await api.delete<any>({ url: `plan/${id}` });
    },
    getTickets: async (page: number, status: string, priority: string): Promise<ApiResponse<TicketData[]>> => {
        return await api.get<TicketData[]>({ url: "tickets", queryParams: { "page": page.toString(), "status": status, "priority": priority } });
    },
    getTicketMessages: async (id: number): Promise<ApiResponse<TicketMessageData[]>> => {
        return await api.get<TicketMessageData[]>({ url: `tickets/${id}` });
    },
    replyToTicket: async (id: number, reply: ReplyToTicketRequest): Promise<ApiResponse<any>> => {
        return await api.post<any>({ url: `tickets/${id}/reply`, body: reply });
    },
    getPayments: async (page: number, limit = 10): Promise<ApiResponse<PaymentData[]>> => {
        return await api.get<PaymentData[]>({
            url: "payments",
            queryParams: {
                page: page.toString(),
                limit: String(Math.min(Math.max(limit, 1), 20)),
            },
        });
    },
    approvePayment: async (id: number): Promise<ApiResponse<void>> => {
        return await api.put<void>({ url: `payments/${id}/approve` });
    },
    rejectPayment: async (id: number): Promise<ApiResponse<PaymentData>> => {
        return await api.put<PaymentData>({ url: `payments/${id}/reject` });
    },
    getDonations: async (page: number, limit = 10, status?: PaymentStatus): Promise<ApiResponse<DonationData[]>> => {
        const queryParams: Record<string, string> = {
            page: page.toString(),
            limit: String(Math.min(Math.max(limit, 1), 20)),
        };
        if (status) {
            queryParams.status = status;
        }
        return await api.get<DonationData[]>({ url: "donations", queryParams });
    },
    getReports: async (page: number, status?: 'resolved' | 'not_resolved'): Promise<ApiResponse<ReportData[]>> => {
        return await api.get<ReportData[]>({ url: "reports", queryParams: { "page": page.toString(), "status": status ?? "" } });
    },
    resolveReport: async (id: number): Promise<ApiResponse<ReportData>> => {
        return await api.put<any>({ url: `reports/${id}/resolve` });
    },
    uploadImage: async (file: File): Promise<ApiResponse<{url: string}>> => {
        const formData = new FormData();
        formData.append("image", file);
        return await api.post<{url: string}>({ url: `upload-image`, formData: formData, headers: { "Content-Type": "multipart/form-data" } });
    },
    getImages: async (page: number): Promise<ApiResponse<string[]>> => {
        return await api.get<string[]>({ url: `images`, queryParams: { "page": page.toString() } });
    },
    deleteImages: async (urls: string[]): Promise<ApiResponse<any>> => {
        return await api.delete<any>({ url: `images`, body: { urls } });
    },
    getSettings: async (): Promise<ApiResponse<SettingData>> => {
        return await api.get<SettingData>({ url: `settings` });
    },
    updateSettings: async (settings: SettingData): Promise<ApiResponse<void>> => {
        return await api.put<void>({ url: `settings`, body: settings });
    },
    getAppVersions: async (page: number): Promise<ApiResponse<AppVersionData[]>> => {
        return await api.get<AppVersionData[]>({ url: `app-versions`, queryParams: { "page": page.toString() } });
    },
    getAppVersion: async (id: number): Promise<ApiResponse<AppVersionData>> => {
        return await api.get<AppVersionData>({ url: `app-versions/${id}` });
    },
    createAppVersion: async (appVersion: CreateAppVersionData): Promise<ApiResponse<any>> => {
        return await api.post<any>({ url: `app-versions`, body: appVersion });
    },
    updateAppVersion: async (id: number, appVersion: CreateAppVersionData): Promise<ApiResponse<any>> => {
        return await api.put<any>({ url: `app-versions/${id}`, body: appVersion });
    },
    deleteAppVersion: async (id: number): Promise<ApiResponse<any>> => {
        return await api.delete<any>({ url: `app-versions/${id}` });
    },
}